import React, { memo, useRef } from 'react'
import styled from 'styled-components'

import { PairLink } from '@root/entities/pair/ui/common'
import { QuickBuyButton } from '@root/features/trade/swap/ui/desktop'

import { PairExploreItemMetrics } from './metrics/pair-explore-item-metrics'
import { PairExploreItemActions } from './pair-explore-item-actions'
import { PairExploreItemAudit } from './pair-explore-item-audit'
import { PairExploreItemChart } from './pair-explore-item-chart'
import { PairExploreItemHeading } from './pair-explore-item-heading'
import { PairExploreItemIcon } from './pair-explore-item-icon'

import type { PairExploreItemBodyProps } from './pair-explore-item-types'
import type { PairModel } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

type Props = PairExploreItemBodyProps & ClassNameProps

export const PairExploreItemBodyLarge: React.FC<Props> = memo(
  ({ pair, statsPeriod, priceChartInterval, onClick, iconSrcOverride }) => {
    const chartContainerRef = useRef<HTMLDivElement>(null)

    return (
      <Root>
        <PairExploreBackgroundLink
          pair={pair}
          onClick={() => onClick?.({ pair })}
        />
        <PairExploreItemIcon
          pair={pair}
          size={84}
          iconSrcOverride={iconSrcOverride}
        />
        <Body>
          <Heading>
            <UpperGroup>
              <PairExploreItemHeading pair={pair} />
              <PairExploreItemActions pair={pair} />
            </UpperGroup>
          </Heading>
          <PairExploreItemMetrics pair={pair} statsPeriod={statsPeriod} />
          <LowerGroup>
            <PairExploreItemAudit pair={pair} size={14} />
          </LowerGroup>
        </Body>
        <QuickBuyButtonWrapper>
          <BuyButton pair={pair} withAmount withLabel section="exploreFeeds" />
          <ChartHolder as="div" ref={chartContainerRef}>
            <PairExploreItemChart pair={pair} interval={priceChartInterval} />
          </ChartHolder>
        </QuickBuyButtonWrapper>
      </Root>
    )
  },
)

const Root = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) 200px;
  align-items: center;
  gap: 10px;
  padding-inline: 12px;
`

const Body = styled.div`
  display: grid;
  gap: 3px;
  padding-block: 12px;
  pointer-events: none;
  z-index: 0;
`

const Heading = styled.div`
  display: grid;
  grid-template-columns: auto auto;
`

const LowerGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
`

const UpperGroup = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  align-items: center;
  justify-content: start;
  justify-items: start;
  gap: 2px;
`

const QuickBuyButtonWrapper = styled.div`
  position: relative;
  display: grid;
  place-items: center;
`

const ChartHolder = styled.div`
  width: 200px;
  height: 90px;

  opacity: 1;
  transition: opacity 0.1s linear;

  ${Root}:hover & {
    opacity: 0.3;
  }
`

const BuyButton = styled(QuickBuyButton)`
  position: absolute;
  z-index: 1;
  place-self: center;
  display: grid;

  opacity: 0;

  &[aria-disabled='true'] {
    opacity: 0;
  }
  transition: opacity 0.1s linear;

  ${Root}:hover & {
    opacity: 1;
  }
`

const PairExploreBackgroundLink = styled(PairLink<PairModel>)`
  position: absolute;
  inset: 0;
`
